import { assertNotEmpty } from '@festina-life/core'
import { useMemo } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type {
  SubTemplateValueSetupField,
  SubTemplateValueSetupFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { TemplateFieldsTable } from './TemplateFieldsTable'

type Props = {
  level: number
  field: SubTemplateValueSetupField
  isCollapsible: boolean
}

export const SingleClassSubTemplateValueSetupTable = ({
  field,
  level,
  isCollapsible,
}: Props) => {
  const {
    preferences: {
      templates: { isShowingEmptyValues },
    },
  } = useLayoutContext()
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  assertNotEmpty(templateVersion)

  const fieldValue = useMemo(
    () =>
      field.value ??
      (isShowingEmptyValues
        ? (generateEmptyFieldValue({
            field,
            meta: metaModel,
          }) as SubTemplateValueSetupFieldValue)
        : undefined),
    [field, isShowingEmptyValues, metaModel],
  )

  const singleSubTemplateVariant = fieldValue?.variants[0]
  const renderedField = useMemo(() => {
    const subTemplatedField = {
      ...field,
      value: fieldValue,
      isRenderingEmpty: isShowingEmptyValues && !field.value && !!fieldValue,
    } as SubTemplateValueSetupField

    subTemplatedField.value?.variants.forEach((variant) => {
      variant.subTemplatedField = subTemplatedField
    })

    return subTemplatedField
  }, [field, fieldValue, isShowingEmptyValues])

  return (
    <TemplateFieldsTable
      level={level}
      parent={renderedField}
      fields={singleSubTemplateVariant?.fields}
      dynamicFunctions={singleSubTemplateVariant?.dynamicFunctions}
      declaredFunctions={singleSubTemplateVariant?.declaredFunctions}
      isCollapsible={isCollapsible}
    />
  )
}
